import { EditedByUser } from 'src/dragon-drop/dragon-drop-node/dragon-drop-node.component';
import { LocationMinified } from 'src/store/location.store';
import { Category } from './category.model';
import { TableTag } from './document.model';
import { DataScope } from './global-enum';
import { ApiRequestParams } from './global-interfaces';
import { ProfileSummary } from './role.model';
import { TaskItem } from './task-item.model';
import { TaskSetMinimum, UpdateTaskSetRequest } from './task-set.model';
import { components } from 'be-types';
import {
  UserMinimum,
  TaskUserPreview,
  RoleResource,
  DefaultAssignee,
  LocalUserRescource,
} from './user.model';

export const taskTableApiRequestParams: ApiRequestParams = {
  dataScope: DataScope.TaskTable,
};
export type TaskDocumentResource = components['schemas']['TaskDocumentResource'];
export type CreateTaskRequest = components['schemas']['StoreTaskRequest'];
export type UpdateTaskRequest = components['schemas']['UpdateTaskRequest'];
export type ReassignTaskRequest = components['schemas']['ReassignTaskRequest'];
export type SpawnTaskRequest = components['schemas']['SpawnedTaskRequest'];
export type CopyTaskRequest = components['schemas']['CopyTaskRequest'];
export type DeployTaskRequest = components['schemas']['DeployTaskRequest'];
export type RecallDeployedTaskRequest = components['schemas']['RecallDeployedTaskRequest'];
export type WorkOnTaskDocumentResource = components['schemas']['TaskDocumentResource'];
export type DelayTaskRequest = components['schemas']['StoreTaskDelayRequest'];
export type SendTaskReminderRequest = components['schemas']['SendTaskReminderRequest'];
export type AssignableTaskResource = components['schemas']['AssignableTaskResource'];
export type CreateScorecardRequest = components['schemas']['CreateScorecardRequest'];
export type UpdateScorecardRequest = components['schemas']['UpdateScorecardRequest'] & {
  // TODO: does BE need this? It's not in the docs
  id: number;
};
export type UpdateTaskNameAdditionRequest = components['schemas']['UpdateTaskNameAdditionRequest'];
export type ScorecardResource = components['schemas']['ScorecardResource'];

export interface FailTaskRequest {
  failedAt: string;
  failedReason: string;
}

export interface ClosedTaskRequest {
  locationID: number;
  completedStartDate?: string;
  completedEndDate?: string;
  dueStartDate?: string;
  dueEndDate?: string;
  taskType?: number;
  scoredTask?: string;
}

export interface ClosedTaskResponse {
  id: number;
  topic: Category;
  name: string;
  type: number;
  nameAddition: string;
  itemNameAddition: string;
  status: string;
  completedAt: string;
  completedBy: UserMinimum;
  failedAt: string;
  failedBy: UserMinimum;
  assignedTo: DefaultAssignee;
  timezone_abbreviation: string;
  tags: string[];
}
export interface ClosedTask {
  taskID: number;
  topic: Category;
  dueAt: string;
  color: number;
  name: string;
  nameAddition: string;
  itemNameAddition: string;
  status: string;
  completedAt: string;
  completedBy: TaskUserPreview;
  failedAt: string;
  failedReason: string;
  failedBy: TaskUserPreview;
  tags: string[];
}

export interface BaseTask {
  id: number;
  name?: string;
  nameAddition?: string;
  itemNameAddition?: string;
  taskSetTemplateID?: number;
}

export interface TaskPreview extends BaseTask {
  approvalStatus: ApprovalStatus;
  assignedTo: DefaultAssignee;
  type: number;
  default_assignee: (UserMinimum | RoleResource)[];
  deploymentStatus: boolean;
  topic: Category;
}

export interface EventTaskInfoResponse {
  dueAt: string;
  timezone_abbreviation?: string;
}

export interface IncompleteTask {
  task_id: number;
  topics: Category[];
  assigned_to: string;
  default_assignee: DefaultAssignee;
}

export interface TaskInfo extends BaseTask {
  allowAssessment: boolean;
  topic?: Category;
  created_at?: string;
  completed_at?: string;
  delayed_to?: string;
  isDelayed?: boolean;
  isGlobal?: boolean;
  failed_at?: string;
  shortName?: string;
  summary?: string;
  purpose?: string;
  builderComments?: string;
  dueAt?: string;
  assigned_to?: DefaultAssignee[];
  createdBy?: string;
  isSpawned?: boolean;
  isDeployed?: boolean;
  isCompleted?: boolean;
  isLocal?: boolean;
  isFailed?: boolean;
  tags?: TableTag[];
  color: number;
  timezone_abbreviation?: string;
}

export interface TaskSetTask extends BaseTask {
  task_set_order: number;
  is_active: boolean;
  type: number;
  default_assignee: (UserMinimum | RoleResource)[];
  isGlobal: boolean;
  isLocalAddedTask: boolean;
  locationID: number;
}

export interface OpenTaskSetTask {
  color: number;
  completedAt: string;
  assignee: DefaultAssignee[];
  dueAt?: string;
  isCompleted: boolean;
  locationID: number;
  name?: string;
  nameAddition?: string;
  id: number;
  type: number;
  topic: Category;
  userIDs: number[];
  failedAt?: string;
  delay?: string;
  timezone_abbreviation?: string;
}

export interface TaskSettings extends BaseTask {
  allowEvents?: boolean;
  allowAssessment?: boolean;
  isDeployed?: boolean;
  is_active?: boolean;
  notifyOnAssignment?: number;
  notifyOnYellow?: number;
  notifyOnRed?: number;
  notifyOnFail?: number;
  task_reoccurs?: TaskReoccurs[];
  turnGreen: number;
  turnRed: number;
  turnYellow: number;
  duration?: UpdateTaskSetRequest['duration'];
  deployed_locations?: DeployedLocations[];
  locations?: LocationMinified[];
  task_documents: TaskDocumentResource[];
  deploy_status?: boolean;
  spawn_status: boolean;
  message?: string;
  roles?: ProfileSummary[];
  users?: LocalUserRescource[];
  defaultDueDate?: string;
  dueAt?: string;
  locationID?: number;
  default_assignee?: (UserMinimum | RoleResource)[];
  isLocalAddedTask: boolean;
  timezone_abbreviation?: string;
  open_task_item_tasks?: number;
  autoScore?: boolean;
  failThreshold?: number;
  passFailActive: boolean;
}

export interface EditTask extends BaseTask {
  taskItems: TaskItem[];
  taskSets: TaskSetMinimum[];
  topic: Category;
  topics: Category[];
  tags: TableTag[];
  type: number;
  availableTags: TableTag[];
  taskSet: TaskSetMinimum;
  default_assignee: (UserMinimum | RoleResource)[];
  deploymentStatus: boolean;
  shortName: string;
  autoScore: boolean;
  totalAllocatedPoints: number;
}

export interface MultiSelectTaskItemOption {
  editedAt: string;
  editedByUser: EditedByUser;
  id: number;
  order: number;
  prompt: string;
  response: string;
  task_id: number;
  // optional will always be false for task item options
  optional: false;
  taskItemID: number;
  itemOptionID: number;
  parentItemTypeId: number;
  itemID: number;
  score?: number;
  hideScoring?: boolean;
}

export interface TaskItemOption {
  itemID: number;
  itemOptionID: number;
  id: number;
  order: number;
  prompt: string;
  taskItemID: number;
  parentItemTypeId: number;
  // optional will always be false for task item options
  optional: false;
  score?: number;
  hideScoring?: boolean;
}

export interface TaskFamilyNode {
  taskID: number;
  name: string;
  nameAddition?: string;
  itemNameAddition?: string;
  level: number;
}

export interface WorkOnTask extends BaseTask {
  taskItems: TaskItem[];
  documents: WorkOnTaskDocumentResource[];
  dueAt: string;
  taskSummary: string;
  profileID: number;
  userID: number;
  timezone_abbreviation?: string;
  taskFamily: TaskFamilyNode[];
  totalScore?: number;
  autoScore: boolean;
}

export interface TaskReoccursBase {
  id?: number;
  type?: number;
  field1?: number;
  field2?: string;
  field3?: string;
  field4?: string;
  dueAt?: string;
  name?: string;
  is_active?: number;
  reoccurAt?: string;
  failedTasksEnabled?: number;
  spawnPrevious?: boolean;
  spawnMultiple?: boolean;
  failedTasksSchedule?: {
    id?: number;
    duration?: number;
    new_tasks?: number;
    time_interval?: string;
  };
}

export interface TaskReoccurs extends TaskReoccursBase {
  startAt?: string;
}
export interface TaskReoccursRequest extends TaskReoccursBase {
  startAt?: string;
}

export enum ApprovalStatus {
  Incomplete = 'incomplete',
  Complete = 'complete',
  Audited = 'audited',
  Approved = 'approved',
  Deployed = 'deployed',
  None = 'none',
}

export interface TaskUpdateTags {
  tags: number[];
  locationID?: number;
}

export interface UnlinkTaskPayload {
  taskID: number;
}
export interface DeployedLocations {
  id: number;
  locationName: string;
  created_at: string;
}

export interface AssignableTaskRequest {
  locationID?: number;
}
